/**
 * What a plan includes — as codes the apps translate. (BW-57)
 *
 * `billing_plans.metadata.features` held English sentences ("Up to 100
 * candidates", "50 Copilot queries/month") that the business billing page
 * printed as they were, so a pt-PT company read its plan in English. A feature
 * is now a code plus, where it has one, the number it states; each app draws it
 * through `PLAN_FEATURE_LABEL_KEY` with that number as `count`.
 */

export const PLAN_FEATURE_CODES = [
  // B2C — Free and Pro
  'checkup_basic',
  'career_basic',
  'teleportal_limited',
  'qultureup_limited',
  'copilot_limited',
  'passport_basic',
  'journeys_unlimited',
  'checkup_full',
  'teleportal_full',
  'qultureup_full',
  'copilot_unlimited',
  'care_advanced',
  'passport_full',
  'career_tools',
  // Family add-on
  'family_members_up_to',
  'journey_access_shared',
  'family_dashboard',
  // B2B — Free and Enterprise
  'seats',
  'seats_unlimited',
  'candidates_up_to',
  'candidates_unlimited',
  'programs',
  'programs_unlimited',
  'cohorts',
  'cohorts_unlimited',
  'copilot_queries_monthly',
  'copilot_queries_unlimited',
  'analytics_basic',
  'analytics_advanced_api',
  'mobility_adaptation_full',
  'yearly_two_months_free',
  // Talent sponsorship
  'sponsorship_full_access',
  'sponsorship_heroes',
  'sponsorship_passport',
  'access_window_months',
  'family_access_optional',
  'discount_vs_free',
  // Support
  'support_email',
  'support_priority',
] as const;

export type PlanFeatureCode = (typeof PLAN_FEATURE_CODES)[number];

/** One line of what a plan includes. `count` is the number it states, if any. */
export interface PlanFeature {
  code: PlanFeatureCode;
  count?: number;
}

/**
 * The features whose label changes with the number ("1 seat" / "3 seats") —
 * their keys carry `_one` / `_other` forms, which the coverage spec checks.
 */
export const PLAN_FEATURES_WITH_PLURALS = [
  'family_members_up_to',
  'seats',
  'candidates_up_to',
  'programs',
  'cohorts',
  'copilot_queries_monthly',
  'access_window_months',
] as const satisfies readonly PlanFeatureCode[];

export const PLAN_FEATURE_LABEL_KEY = {
  checkup_basic: 'billing.planFeature.checkup_basic',
  career_basic: 'billing.planFeature.career_basic',
  teleportal_limited: 'billing.planFeature.teleportal_limited',
  qultureup_limited: 'billing.planFeature.qultureup_limited',
  copilot_limited: 'billing.planFeature.copilot_limited',
  passport_basic: 'billing.planFeature.passport_basic',
  journeys_unlimited: 'billing.planFeature.journeys_unlimited',
  checkup_full: 'billing.planFeature.checkup_full',
  teleportal_full: 'billing.planFeature.teleportal_full',
  qultureup_full: 'billing.planFeature.qultureup_full',
  copilot_unlimited: 'billing.planFeature.copilot_unlimited',
  care_advanced: 'billing.planFeature.care_advanced',
  passport_full: 'billing.planFeature.passport_full',
  career_tools: 'billing.planFeature.career_tools',
  family_members_up_to: 'billing.planFeature.family_members_up_to',
  journey_access_shared: 'billing.planFeature.journey_access_shared',
  family_dashboard: 'billing.planFeature.family_dashboard',
  seats: 'billing.planFeature.seats',
  seats_unlimited: 'billing.planFeature.seats_unlimited',
  candidates_up_to: 'billing.planFeature.candidates_up_to',
  candidates_unlimited: 'billing.planFeature.candidates_unlimited',
  programs: 'billing.planFeature.programs',
  programs_unlimited: 'billing.planFeature.programs_unlimited',
  cohorts: 'billing.planFeature.cohorts',
  cohorts_unlimited: 'billing.planFeature.cohorts_unlimited',
  copilot_queries_monthly: 'billing.planFeature.copilot_queries_monthly',
  copilot_queries_unlimited: 'billing.planFeature.copilot_queries_unlimited',
  analytics_basic: 'billing.planFeature.analytics_basic',
  analytics_advanced_api: 'billing.planFeature.analytics_advanced_api',
  mobility_adaptation_full: 'billing.planFeature.mobility_adaptation_full',
  yearly_two_months_free: 'billing.planFeature.yearly_two_months_free',
  sponsorship_full_access: 'billing.planFeature.sponsorship_full_access',
  sponsorship_heroes: 'billing.planFeature.sponsorship_heroes',
  sponsorship_passport: 'billing.planFeature.sponsorship_passport',
  access_window_months: 'billing.planFeature.access_window_months',
  family_access_optional: 'billing.planFeature.family_access_optional',
  discount_vs_free: 'billing.planFeature.discount_vs_free',
  support_email: 'billing.planFeature.support_email',
  support_priority: 'billing.planFeature.support_priority',
} as const satisfies Record<PlanFeatureCode, string>;

/** A stored feature line, validated — `metadata` is jsonb, so read it as such. */
export function isPlanFeature(value: unknown): value is PlanFeature {
  if (typeof value !== 'object' || value === null) return false;
  const { code, count } = value as { code?: unknown; count?: unknown };
  return (
    typeof code === 'string' &&
    (PLAN_FEATURE_CODES as readonly string[]).includes(code) &&
    (count === undefined || (typeof count === 'number' && Number.isFinite(count)))
  );
}
